import {
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  LockClosedIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import InputField from "../common11/formInputs/InputField";
import SearchDropdown from "../common11/formInputs/SearchDropdown";
import {
  clearError,
  loginStart,
  loginSuccess,
} from "../../store/slices/AuthSlice";

const LOGIN_BG = `${process.env.PUBLIC_URL || ""}/login-bg.png`;
const LOGIN_LOGO = `${process.env.PUBLIC_URL || ""}/logo-square.svg`;

const MOCK_UNITS = [
  { id: 1, label: "Main Hospital", value: 1 },
  { id: 2, label: "City Clinic", value: 2 },
];

const MOCK_CASH_COUNTERS = [
  { id: 1, label: "OPD Counter 1", value: 1 },
  { id: 2, label: "IPD Counter 2", value: 2 },
];

const validationSchema = yup.object({
  loginId: yup.string().required("User name is required").min(3),
  password: yup.string().required("Password is required").min(4),
  unit: yup.object().nullable().required("Please select a unit"),
  cashCounter: yup.object().nullable().required("Please select a cash counter"),
});

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoggedIn, loading: authLoading } = useSelector((state) => state.auth);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      loginId: "",
      password: "",
      unit: MOCK_UNITS[0],
      cashCounter: MOCK_CASH_COUNTERS[0],
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/dashboard", { replace: true });
    }
  }, [isLoggedIn, navigate]);

  const onSubmit = async (data) => {
    dispatch(clearError());
    dispatch(loginStart());

    // Temporary: skip /auth/User/login until real auth is wired up.
    // Any valid form submission logs in locally (works the same in local + deployed builds).
    const userData = {
      userId: 1,
      userName: data.loginId?.trim() || "Admin",
      loginName: data.loginId?.trim() || "Admin",
      loginId: data.loginId?.trim(),
      unitId: data.unit?.id || data.unit?.value || 1,
      unitName: data.unit?.label || "Main Hospital",
      cashCounterId: data.cashCounter?.id || data.cashCounter?.value || 1,
      cashCounterName: data.cashCounter?.label || "OPD Counter 1",
      groupId: 0,
      loginTime: new Date().toISOString(),
    };
    dispatch(loginSuccess(userData));
    toast.success(`Welcome back, ${userData.userName}!`);
    navigate("/dashboard", { replace: true });
  };

  const isLoading = isSubmitting || authLoading;

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center lg:justify-end px-4 sm:px-6 py-6"
      style={{
        backgroundImage: `url(${LOGIN_BG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full flex justify-center lg:justify-end lg:pr-[8.8%]">
        <div className="w-full max-w-[460px] bg-[#EAD9B2]/10 backdrop-blur-sm rounded-2xl shadow-2xl p-5 sm:p-6">
          <div className="flex justify-center mb-2">
            <img
              src={LOGIN_LOGO}
              alt="Shushrut System"
              className="w-20 h-20 object-contain"
            />
          </div>

          <h2 className="text-base font-semibold text-center text-gray-700 mb-3">
            Login into your account
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <InputField
              name="loginId"
              label="User name"
              type="text"
              control={control}
              placeholder="Enter user name"
              autoComplete="username"
              disabled={isLoading}
              rightIcon={<UserIcon className="text-white w-4 h-4" />}
              iconBgColor="bg-[#217E90]"
              isFloatingLabel={false}
              inputSize="h-10"
              fontSize="text-sm"
            />

            <InputField
              name="password"
              label="Password"
              type="password"
              control={control}
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={isLoading}
              showPasswordIcon
              rightIcon={<LockClosedIcon className="text-white w-4 h-4" />}
              iconBgColor="bg-[#217E90]"
              isFloatingLabel={false}
              inputSize="h-10"
              fontSize="text-sm"
            />

            <SearchDropdown
              name="unit"
              label="Select Unit"
              control={control}
              dataArray={MOCK_UNITS}
              disabled={isLoading}
              rightIcon={<BuildingOfficeIcon className="text-white w-4 h-4" />}
              iconBgColor="bg-[#217E90]"
              placeholder="Choose a unit"
              isFloatingLabel={false}
              selectSize="h-10"
              fontSize="text-sm"
            />

            <SearchDropdown
              name="cashCounter"
              label="Select Cash Counter"
              control={control}
              dataArray={MOCK_CASH_COUNTERS}
              disabled={isLoading}
              rightIcon={<CurrencyDollarIcon className="text-white w-4 h-4" />}
              iconBgColor="bg-[#217E90]"
              placeholder="Choose a cash counter"
              isFloatingLabel={false}
              selectSize="h-10"
              fontSize="text-sm"
            />

            <div className="relative flex items-center my-2">
              <div className="flex-grow border-t border-gray-300" />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#217E90] text-white py-2.5 px-4 rounded-md hover:bg-[#1A5B63] transition duration-200 font-medium disabled:opacity-50"
            >
              {isLoading ? "Logging in..." : "Login"}
            </button>
          </form>

          <div className="mt-3 text-center text-gray-500 text-xs">
            © {new Date().getFullYear()} Shushrut System. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
